import axios from 'axios';
import {
  ID,
  ListApiResponse,
  SingleApiResponse
} from '@ianesthesie-kit/services';
import { stringifyParams } from './billing.queryParams';
import {
  DeskPayment,
  DeskPaymentPreview,
  ResolveInterventionOutcomePayload,
  StartDeskPaymentPayload
} from './deskPayment.model';

/**
 * BFF client for the card-terminal payments of a billing line (`/billing/desk-payment`). The
 * hospital and the calling user travel via headers — never sent from here. Static-only,
 * following {@link BillingEntityService}'s surface.
 */
export class DeskPaymentService {
  private static BASE_URL = '/billing/desk-payment';

  /** Amounts, billing entity and readers behind the dialog's recap, plus its eligibility gate. */
  public static fetchPreview(lineId: ID): Promise<DeskPaymentPreview> {
    return axios
      .get<SingleApiResponse<DeskPaymentPreview>>(
        `${this.BASE_URL}/line/${lineId}/preview`
      )
      .then((resp) => resp.data.data);
  }

  /** Creates the payment intent and sends it to the reader; the returned payment is `CREATED`. */
  public static start(
    lineId: ID,
    payload: StartDeskPaymentPayload
  ): Promise<DeskPayment> {
    return axios
      .post<SingleApiResponse<DeskPayment>>(
        `${this.BASE_URL}/line/${lineId}`,
        payload
      )
      .then((resp) => resp.data.data);
  }

  /** Re-reads Stripe and advances the state machine — the dialog's poll. */
  public static refresh(paymentIntentId: ID): Promise<DeskPayment> {
    return axios
      .post<SingleApiResponse<DeskPayment>>(
        `${this.BASE_URL}/${paymentIntentId}/refresh`
      )
      .then((resp) => resp.data.data);
  }

  /**
   * Settles or releases the hold a two-bite payment left on the card, once the desk knows
   * whether the intervention took place.
   */
  public static resolveInterventionOutcome(
    paymentIntentId: ID,
    payload: ResolveInterventionOutcomePayload
  ): Promise<DeskPayment> {
    return axios
      .post<SingleApiResponse<DeskPayment>>(
        `${this.BASE_URL}/${paymentIntentId}/outcome`,
        payload
      )
      .then((resp) => resp.data.data);
  }

  public static cancel(paymentIntentId: ID): Promise<DeskPayment> {
    return axios
      .post<SingleApiResponse<DeskPayment>>(
        `${this.BASE_URL}/${paymentIntentId}/cancel`
      )
      .then((resp) => resp.data.data);
  }

  /** Every desk payment of a patient, newest first. */
  public static fetchByPatient(patientId: ID): Promise<DeskPayment[]> {
    const qs = stringifyParams({ patientId });
    return axios
      .get<ListApiResponse<DeskPayment>>(`${this.BASE_URL}${qs}`)
      .then((resp) => resp.data.data);
  }
}
